import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDeleteLaborMutation } from '@/features/workorders/workorders.hooks';
import { WorkorderLabor } from '@certhon/models';
import { Lock, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { StackLink } from '@/app/navigation/StackLink';
import { formatDate, formatHours, formatTime } from '../workorders.utils';

interface WorkorderLaborTableProps {
  workorderRecnum: number;
  /** Path of the workorder details panel; entry links are built below it. */
  basePath: string;
  labor: WorkorderLabor[];
  isLoading: boolean;
  isLocked?: boolean;
}

function calculateHours(entry: WorkorderLabor): number {
  const start = new Date(entry.date_start).getTime();
  const stop = new Date(entry.date_stop).getTime();
  const breakHours = entry.break ?? 0;
  return Math.max(0, (stop - start) / (1000 * 60 * 60) - breakHours);
}

export function WorkorderLaborTable({
  workorderRecnum,
  basePath,
  labor,
  isLoading,
  isLocked: workorderLocked = false,
}: WorkorderLaborTableProps) {
  const deleteMutation = useDeleteLaborMutation(workorderRecnum);

  const totalHours = useMemo(() => {
    return labor.reduce((sum, entry) => sum + calculateHours(entry), 0);
  }, [labor]);

  const handleDelete = (recnum: number) => {
    if (confirm('Are you sure you want to delete this labor entry?')) {
      deleteMutation.mutate(recnum);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!workorderLocked && (
        <div className="flex justify-end">
          <Button asChild size="sm" tooltip={false}>
            <StackLink to={`${basePath}/labor/new`}>
              <Plus className="mr-1 h-4 w-4" />
              Add Labor
            </StackLink>
          </Button>
        </div>
      )}

      {labor.length === 0 ? (
        <div className="text-muted-foreground py-8 text-center">
          No labor entries yet
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Break</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Day Fee</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {labor.map((entry) => {
              const hours = calculateHours(entry);
              const isEntryLocked = entry.locked || workorderLocked;

              return (
                <TableRow
                  key={entry.recnum}
                  className={isEntryLocked ? 'bg-muted/50' : undefined}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {entry.locked && (
                        <Lock className="text-muted-foreground h-3 w-3" />
                      )}
                      {formatDate(entry.date_start)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatTime(entry.date_start)} -{' '}
                    {formatTime(entry.date_stop)}
                  </TableCell>
                  <TableCell>
                    {entry.break ? `${entry.break * 60}m` : '—'}
                  </TableCell>
                  <TableCell>{formatHours(hours)}</TableCell>
                  <TableCell>{entry.dayfee ? 'Yes' : '—'}</TableCell>
                  <TableCell>
                    {!isEntryLocked && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <StackLink to={`${basePath}/labor/${entry.recnum}`}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </StackLink>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(entry.recnum)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="font-medium">
                Total
              </TableCell>
              <TableCell className="font-medium">
                {formatHours(totalHours)}
              </TableCell>
              <TableCell colSpan={2}></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </div>
  );
}
